import { useSelector } from 'react-redux';
import { usePlaidLink } from 'react-plaid-link';
import React, { useState, useEffect, Fragment } from 'react';

import {
    Box,
    Grid,
    Divider,
    Accordion,
    Typography,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import BuildIcon from '@mui/icons-material/Build';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { Button, SnackBarAlert } from 'shared';
import { snackAlertData } from 'utils/constants';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { manageBankConnectionsStyles } from '../style/style';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { updatePlaidToken, getPlaidUpdateLink } from 'store/services/bank.Service';

const LinkedAccountsAccordion = ({ data, fetchBankAccounts }) => {
    const classes = manageBankConnectionsStyles();
    const { id: companyId } = useSelector((state) => state.company.data);

    const [expanded, setExpanded] = useState(true);
    const [linkToken, setLinkToken] = useState(null);
    const [plaidItemId, setPlaidItemId] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);

    const handleGetPlaidUpdateLink = async (plaidItemId) => {
        try {
            setUpdateLoading(true);
            setPlaidItemId(plaidItemId);
            const response = await getPlaidUpdateLink({
                company_id: companyId,
                plaid_item_id: plaidItemId,
            });
            setLinkToken(response?.link_token);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const onSuccess = async (public_token, metadata) => {
        try {
            const response = await updatePlaidToken({
                company_id: companyId,
                public_token: public_token,
                plaid_item_id: plaidItemId,
            });
            handleSuccessAlert(setAlertData, response.message);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setUpdateLoading(false);
            setPlaidItemId(null);
            fetchBankAccounts();
        }
    };

    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess,
    });

    useEffect(() => {
        if (ready) {
            open();
        }
    }, [ready, open]);

    return (
        <Box sx={classes.accordionWrapper}>
            <Box mt={2}>
                <Accordion
                    expanded={expanded}
                    sx={classes.accordion}
                    onChange={() => setExpanded(!expanded)}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <Grid item marginLeft="10px">
                                    <AccountBalanceIcon fontSize="large" />
                                </Grid>
                                <Grid item>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {data.bankName}{' '}
                                        {data?.loginRequired ? (
                                            <ErrorIcon fontSize="12px" color="error" />
                                        ) : (
                                            <CheckCircleIcon fontSize="12px" color="success" />
                                        )}
                                    </Typography>
                                    <Typography variant="caption">
                                        Added on {data.addedOn}
                                    </Typography>
                                </Grid>
                            </Box>
                            {data?.loginRequired && (
                                <Grid item>
                                    <Button
                                        text="Update"
                                        endIcon={<BuildIcon fontSize="12px" />}
                                        customStyles={classes.updateButton}
                                        loading={updateLoading}
                                        onClick={() => handleGetPlaidUpdateLink(data?.id)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </AccordionSummary>

                    <AccordionDetails>
                        {data.accounts.map((account, index) => (
                            <Fragment key={index}>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    py={1}
                                >
                                    <Grid item>
                                        <Typography variant="body1">
                                            {account.name}{' '}
                                            {account?.loginRequired ? (
                                                <ErrorIcon fontSize="12px" color="error" />
                                            ) : (
                                                <CheckCircleIcon fontSize="12px" color="success" />
                                            )}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="body2"
                                            color={account.balance < 0 ? 'error' : 'text.primary'}
                                        >
                                            $
                                            {account.balance.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                {index !== data.accounts.length - 1 && <Divider />}
                            </Fragment>
                        ))}
                    </AccordionDetails>
                </Accordion>
            </Box>

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Box>
    );
};

export default LinkedAccountsAccordion;
